import { ApprovalCall } from "@cleo/contracts";
import * as Option from "effect/Option";
import * as Schema from "effect/Schema";

// The Inbox's rows as the World's door gives them (neucleos_list_gate_instances, neucleos_decide_gate_instance), shaped
// once for every host that reads them: the Desk's server (apps/server worldDoorHttp.ts, which then adds what only that
// Mac knows: its stop record and its own conversations' request words) and a window with no Mac behind it (cleo-web,
// apps/web webHostWorldDoors.ts, steps 5-7 design §12c). Nothing here decides anything: each value is the World's, read
// field by field, and a row without its three keys is no row.

const decodeApprovalCall = Schema.decodeUnknownOption(ApprovalCall);

/** A card's request named by the World: its exact call, shown and approved as Cleo's own World tool call. */
export interface WorldGateRequest {
  readonly detail: string;
  readonly call: ApprovalCall;
  readonly conversationTitle: null;
}

/**
 * Can 47: a World conversation keeps its call in the World, not on any Mac. The World names a waiting one with its exact
 * call, read from that conversation's own record and verified (backend lib/cloudCallRead.js), and it becomes the card's
 * request: a World tool call, described in the Inbox's own words like any other. It is Cleo's own action (deskOwnServer):
 * the World's record says the call was made to the World's own /mcp door by the conversation's own step (the occurrence
 * law), which is established by the World, never by a tool's or server's name. Anything else is no request.
 */
export function cloudCallRequest(value: unknown): WorldGateRequest | null {
  if (value === null || typeof value !== "object" || Array.isArray(value)) return null;
  const { tool, args } = value as { tool?: unknown; args?: unknown };
  if (
    typeof tool !== "string" ||
    tool === "" ||
    args === null ||
    typeof args !== "object" ||
    Array.isArray(args)
  )
    return null;
  const call = Option.getOrUndefined(
    decodeApprovalCall({ kind: "tool", tool, args, deskOwnServer: true }),
  );
  return call === undefined ? null : { detail: "", call, conversationTitle: null };
}

/** A cancelled gate row's reason and the World's sentence for it, each only when the World gave it as a non-empty string. */
export function gateCancellationFields(r: Record<string, unknown>): Record<string, string> {
  return {
    ...(typeof r["invalidatedReason"] === "string" && r["invalidatedReason"] !== ""
      ? { invalidatedReason: r["invalidatedReason"] }
      : {}),
    ...(typeof r["cancelledSentence"] === "string" && r["cancelledSentence"] !== ""
      ? { cancelledSentence: r["cancelledSentence"] }
      : {}),
  };
}

/** One listed row as the Inbox reads it, or null when it lacks its gateKey, gateInstanceKey or targetDigest. */
export function worldGateListRow(value: unknown): Record<string, unknown> | null {
  if (typeof value !== "object" || value === null) return null;
  const r = value as Record<string, unknown>;
  if (
    typeof r["gateKey"] !== "string" ||
    typeof r["gateInstanceKey"] !== "string" ||
    typeof r["targetDigest"] !== "string"
  )
    return null;
  return {
    gateKey: r["gateKey"],
    gateInstanceKey: r["gateInstanceKey"],
    targetDigest: r["targetDigest"],
    title: typeof r["title"] === "string" ? r["title"] : r["gateKey"],
    kind: typeof r["kind"] === "string" ? r["kind"] : "",
    deciderClass: typeof r["deciderClass"] === "string" ? r["deciderClass"] : "",
    state: typeof r["state"] === "string" ? r["state"] : "",
    outcome: typeof r["outcome"] === "string" ? r["outcome"] : null,
    humanDecides: r["humanDecides"] === true,
    needsAttention: r["needsAttention"] === true,
    decider: r["decider"] ?? "self",
    effectStatus: r["effectStatus"] ?? "not-recorded",
    // A request the World cancelled (the person's Stop withdrew it, or its cloud turn ended): why, and the World's own
    // sentence, which the card shows in place of Approve.
    ...gateCancellationFields(r),
    threadId: typeof r["threadId"] === "string" ? r["threadId"] : null,
    sessionId: typeof r["sessionId"] === "string" ? r["sessionId"] : null,
    createdAt: typeof r["createdAt"] === "number" ? r["createdAt"] : null,
    // Can 73: the World marks every gate a member change raises with its key; the page keeps Approve waiting on that
    // change's card, so the mark must reach it.
    ...(typeof r["memberChangeKey"] === "string" && r["memberChangeKey"] !== ""
      ? { memberChangeKey: r["memberChangeKey"] }
      : {}),
    // The World's card-only mark (lib/worldAuth.js projectGateInstance): the Inbox keeps Approve on its card alone.
    ...(r["cardApproved"] === true ? { cardApproved: true } : {}),
    // Can 47: what the card says, read by the World when it opened the approval (backend lib/approvalCardFacts.js).
    ...(r["cardFacts"] !== undefined && r["cardFacts"] !== null
      ? { cardFacts: r["cardFacts"] }
      : {}),
    // 7.4.4b: a Canvas switch press's card names the placement the World verified was pressed.
    ...pressPlacement(r["pressCall"]),
  };
}

function pressPlacement(value: unknown): {
  press?: { canvasId: string; sequence: number; nodeId: string; requestDigest?: string };
} {
  const press = (value as { press?: unknown } | null | undefined)?.press as
    | Record<string, unknown>
    | undefined;
  const requestDigest = press?.["requestDigest"];
  return press &&
    typeof press["canvasId"] === "string" &&
    typeof press["sequence"] === "number" &&
    typeof press["nodeId"] === "string"
    ? {
        press: {
          canvasId: press["canvasId"],
          sequence: press["sequence"],
          nodeId: press["nodeId"],
          // The sha256 of the press's request key: which press this card is, among presses of one placement.
          ...(typeof requestDigest === "string" && /^[0-9a-f]{64}$/u.test(requestDigest)
            ? { requestDigest }
            : {}),
        },
      }
    : {};
}

export type WorldGateList =
  | {
      readonly ok: true;
      readonly state: string;
      readonly nextCursor: string | null;
      readonly attentionCount: number;
      /** Each row with the World's own request beside it (cloudCall), when the World named one. */
      readonly rows: ReadonlyArray<{
        readonly row: Record<string, unknown>;
        readonly cloud: WorldGateRequest | null;
      }>;
    }
  | { readonly ok: false; readonly reason: string };

/** The door's list answer ({instances, state, nextCursor, attentionCount}), every row shaped, or why it is not one. */
export function worldGateList(value: unknown): WorldGateList {
  const raw = value as {
    instances?: unknown;
    state?: unknown;
    nextCursor?: unknown;
    attentionCount?: unknown;
  } | null;
  if (raw === null || typeof raw !== "object" || !Array.isArray(raw.instances))
    return { ok: false, reason: "The World door returned an invalid gate-instance list." };
  const rows: Array<{ row: Record<string, unknown>; cloud: WorldGateRequest | null }> = [];
  for (const item of raw.instances as unknown[]) {
    if (typeof item !== "object" || item === null)
      return { ok: false, reason: "The World door returned an invalid gate-instance row." };
    const row = worldGateListRow(item);
    if (row === null)
      return { ok: false, reason: "The World door returned a gate-instance row without its keys." };
    // A World conversation's call (cloudCall), or a Canvas switch press's call the World verified (pressCall, 7.4.4b-1a).
    const record = item as Record<string, unknown>;
    rows.push({
      row,
      cloud: cloudCallRequest(record["cloudCall"]) ?? cloudCallRequest(record["pressCall"]),
    });
  }
  return {
    ok: true,
    state: typeof raw.state === "string" ? raw.state : "CREATED",
    nextCursor: typeof raw.nextCursor === "string" ? raw.nextCursor : null,
    attentionCount: typeof raw.attentionCount === "number" ? raw.attentionCount : 0,
    rows,
  };
}

/**
 * The door's answer to a decision, as the decided row, or null when it is not the instance asked about (its key or its
 * targetDigest differ, or it names no gate).
 */
export function worldGateDecidedRow(
  value: unknown,
  gateInstanceKey: string,
  targetDigest: string,
): Record<string, unknown> | null {
  if (value === null || typeof value !== "object") return null;
  const r = value as Record<string, unknown>;
  if (
    r["gateInstanceKey"] !== gateInstanceKey ||
    r["targetDigest"] !== targetDigest ||
    typeof r["gateKey"] !== "string"
  )
    return null;
  return {
    gateKey: r["gateKey"],
    gateInstanceKey,
    targetDigest,
    kind: typeof r["kind"] === "string" ? r["kind"] : "",
    deciderClass: typeof r["deciderClass"] === "string" ? r["deciderClass"] : "",
    state: typeof r["state"] === "string" ? r["state"] : "",
    outcome: typeof r["outcome"] === "string" ? r["outcome"] : null,
    decider: r["decider"] ?? "self",
    effectStatus: r["effectStatus"] ?? "not-recorded",
    ...gateCancellationFields(r),
    decidedAt: typeof r["decidedAt"] === "number" ? r["decidedAt"] : null,
    createdAt: typeof r["createdAt"] === "number" ? r["createdAt"] : null,
  };
}
